import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentData } from "firebase/firestore";
import { insightsCollection } from "./insightsCollection";
import { db, auth, signOut, getImageFromStorage } from "./firebase";
import { logAppEvent } from "./Helpers/EventLogging";
import "./Homepage.css"; // Import the CSS file for styling
import { FaPlus, FaBars, FaExternalLinkAlt } from "react-icons/fa"; // Import the plus, bars, and external link icons
import TemplateCard from "./Components/TemplateCard";
import IntegrationCard from "./Components/IntegrationCard";
import FlowCard from "./Components/FlowCard";
import { Flow, LawyerData, Template } from "./types/types";
// import { Tab, Tabs, TabList, TabView, TabPanel } from "react-tabs"; // Import react-tabs
import "react-tabs/style/react-tabs.css"; // Import react-tabs CSS
import BurgerMenu from "./Components/BurgerMenu"; // Import the new BurgerMenu component
import { TabView, TabPanel } from "primereact/tabview";
import { DataView, DataViewLayoutOptions } from "primereact/dataview";
import { formatTimestamp } from "./Helpers/Calculations";
import placeholder from "./assets/focusmap-placeholder.png";
import { deleteFlowData } from "./storage/storageProxy";
import "./styles/focusmap-theme.css";

import {
  fetchFlows,
  getUserPlan,
  getBusinessModuleSubscriber,
  getUserDoc,
  checkUserPlanAndFlowLimit,
  getUserDataByEmail,
  updateUserShowWelcome,
  updateUserUseLocalData,
  loadMyTemplates,
} from "./storage/storageProxy"; // Import fetchFlows from firestoreService
import { IoHelpCircle } from "react-icons/io5"; // Import the help icon
import { FaCloud, FaDatabase } from "react-icons/fa";
import HelpModal from "./Components/HelpModal"; // Import the HelpModal component
import CustomAlert from "./Components/CustomAlert"; // Import CustomAlert component
import WelcomeModal from "./Components/WelcomeModal"; // Import the WelcomeModal component
// import { templates } from "./templates";
import { integrations } from "./Data/integrations";
import OneNoteIntegrationModal from "./Components/OneNoteIntegrationModal"; // Import the new OneNoteIntegrationModal component
import LaywerTemplate from "./Components/SystemTemplates/LawyerTemplate"; // Import the LawyerTemplate component
import devBanner from "./assets/images/preview.png"; // Import the dev-banner image
import { gt, set } from "lodash";
import { useStore } from "./App/store";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { PrimeIcons } from "primereact/api";
import { doc, updateDoc } from "firebase/firestore";

interface FlowCardProps {
  flow: Flow;
  handleFlowClick: (id: string) => void;
}

const Homepage: React.FC = () => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [businessTemplates, setBusinessTemplates] = useState<Template[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showLawyerTemplate, setShowLawyerTemplate] = useState(false);
  const [selectedHelpTopic, setSelectedHelpTopic] = useState<string | null>(
    null
  );
  const [showMapLimitAlert, setShowMapLimitAlert] = useState(false);
  const [showFeatureAlert, setShowFeatureAlert] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [showOneNoteModal, setShowOneNoteModal] = useState(false);
  const [layout, setLayout] = useState<"grid" | "list">("list");
  const [alertTitle, setAlertTitle] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showRenameConfirmModal, setShowRenameConfirmModal] = useState(false);
  const [flowName, setFlowName] = useState("");
  const [currentFlowId, setCurrentFlowId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const user = auth.currentUser;

  const menuRef = useRef<HTMLDivElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLDivElement>,
    flowId: string | null,
    action: string
  ) => {
    event.stopPropagation();
    setCurrentFlowId(flowId);
    if (action === "rename") {
      setShowRenameConfirmModal(true);
    } else if (action === "delete") {
      setShowDeleteConfirmModal(true);
    } else {
      alert("Not yet available");
    }
    setShowMenu(false);
  };

  const handleConfirmDelete = async (event: React.MouseEvent) => {
    try {
      await deleteFlowData(currentFlowId, useStore.getState().useLocalStorage);
    } catch (error) {
      console.error("Error deleting document: ", error);
      alert("Failed to delete document");
    }
    event.stopPropagation();
    setShowDeleteConfirmModal(false);
    loadFlows();
  };

  const handleCancelDelete = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDeleteConfirmModal(false);
  };

  const handleConfirmRename = async (
    event: React.MouseEvent,
    renamed: string
  ) => {
    event.stopPropagation();
    try {
      await updateDoc(doc(db, "flows", currentFlowId), { name: renamed });
      setFlowName(renamed);
    } catch (error) {
      console.error("Error renaming document: ", error);
      alert("Failed to rename document");
    }
    setShowRenameConfirmModal(false);
  };

  const handleCancelRename = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowRenameConfirmModal(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    loadFlows();
    logAppEvent("activity", { type: "load", name: "HomePage" });
  }, [user, useStore.getState().useLocalStorage]);

  const loadFlows = async () => {
    if (user) {
      if (user.email) {
        setFlows([]);
        const flowsData = await fetchFlows(
          user.email,
          useStore.getState().useLocalStorage
        );
        const sortedData = sortData(flowsData);
        setFlows(sortedData as Flow[]);
      }

      fetchMyTemplates();

      fetchAvailableSources();
    }
  };

  const fetchMyTemplates = async () => {
    if (user) {
      if (user.email) {
        await loadMyTemplates(user?.email, useStore.getState().useLocalStorage)
          .then((templates: Template[]) => {
            const sortedTemplates = templates.sort(
              (a: Template, b: Template) => {
                const dateA = new Date(a.timestamp);
                const dateB = new Date(b.timestamp);

                // Validate if date is Invalid
                if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                  throw new Error("Invalid date format detected");
                }
                return dateB.getTime() - dateA.getTime(); // Descending order
              }
            );
            setTemplates(sortedTemplates as Template[]);
          })
          .catch((error) => {
            console.error("Error loading templates: ", error);
          });
      }
    }
  };

  const fetchBusinessTemplates = async () => {
    // if (user) {
    //   if (user.email) {
    //     await loadMyTemplates(
    //       user?.email,
    //       useStore.getState().useLocalStorage
    //     )
    //       .then((templates: Template[]) => {
    //         const sortedTemplates = templates.sort(
    //           (a: Template, b: Template) => {
    //             const dateA = new Date(a.timestamp);
    //             const dateB = new Date(b.timestamp);
    //             // Validate if date is Invalid
    //             if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
    //               throw new Error("Invalid date format detected");
    //             }
    //             return dateB.getTime() - dateA.getTime(); // Descending order
    //           }
    //         );
    //         setTemplates(sortedTemplates as Template[]);
    //       })
    //       .catch((error) => {
    //         console.error("Error loading templates: ", error);
    //       });
    //   }
    // }
  };

  const fetchAvailableSources = async () => {
    if (user) {
      const userDocument = await getUserDoc(user.uid, false);
      const availableSources = insightsCollection.map((item) => item.data.key);
      const userSelectedSources = Object.keys(
        userDocument?.insights || {}
      ).filter((key) => userDocument?.insights[key].checked);
      // console.log("User selected sources", userSelectedSources);

      // Always include Brainstorm
      const userInsights = availableSources.filter(
        (source: string) =>
          source === "brainstorm" || // Always include brainstorm
          (Array.isArray(userSelectedSources) &&
            userSelectedSources.includes(source))
      );
      const updatedUserInsights = userInsights.map((source: string) => {
        const matchingItem = insightsCollection.find(
          (item) => item.data.key === source
        );
        return matchingItem ? matchingItem.data.title : source;
      });
      // console.log("Updated User Insights", updatedUserInsights);
      useStore.getState().setSourceOptions(updatedUserInsights);
    }
  };

  const reSortData = (id: string, isFavourite: boolean) => {
    const updatedFlows = flows.map((flow) => {
      if (flow.id === id) {
        return { ...flow, favourite: isFavourite };
      }
      return flow;
    });
    const sortedData = sortData(updatedFlows);
    setFlows(sortedData);
  };

  const sortData = (data: any) => {
    data.sort((a: Flow, b: Flow) => {
      if (a.favourite && !b.favourite) return -1;
      if (!a.favourite && b.favourite) return 1;

      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);

      // Validate if date is Invalid
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        throw new Error("Invalid date format detected");
      }
      return dateB.getTime() - dateA.getTime(); // Descending order
    });
    return data;
  };

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
  };

  const handleCloseAlert = () => {
    setAlertTitle(null);
    setAlertMessage(null);
  };

  useEffect(() => {
    const checkShowWelcome = async () => {
      if (user) {
        const userDoc = await getUserDoc(
          user.uid,
          useStore.getState().useLocalStorage
        );
        if (userDoc && userDoc.showWelcome) {
          setShowWelcomeModal(true);
        }
      }
    };

    checkShowWelcome();
  }, [user]);

  useEffect(() => {
    //setShowHelpModal(true);
  }, [showHelpModal]);

  const handleFlowClick = (flowId: string) => {
    navigate(`/flow/${flowId}`);
  };

  const handleIntegrationClick = async (templateId: string) => {
    if (user) {
      const userPlan = await getUserPlan(
        user,
        useStore.getState().useLocalStorage
      );
      if (userPlan && userPlan.includes("free")) {
        setShowFeatureAlert(true);
        return;
      }
    }

    setShowOneNoteModal(true);
  };

  const handleLawyerIntegrationClick = async (templateId: string) => {
    if (user) {
      const subscriber = await getBusinessModuleSubscriber(
        user.email!,
        templateId,
        useStore.getState().useLocalStorage
      );
      if (!subscriber) {
        setShowFeatureAlert(true);
        return;
      }
    }
    setShowLawyerTemplate(true);
  };

  const handleSustainabilityIntegrationClick = async (templateId: string) => {
    if (user) {
      const subscriber = await getBusinessModuleSubscriber(
        user.email!,
        templateId,
        useStore.getState().useLocalStorage
      );
      if (!subscriber) {
        setShowFeatureAlert(true);
        return;
      }
    }
    handleCreateNewFlow(templateId);
  };

  const handleMicrosoft365IntegrationClick = async (templateId: string) => {
    if (user) {
      const subscriber = await getBusinessModuleSubscriber(
        user.email!,
        templateId,
        useStore.getState().useLocalStorage
      );
      if (!subscriber) {
        setShowFeatureAlert(true);
        return;
      }
    }
    handleCreateNewFlow(templateId);
  };

  const handleRapidLearningCyclesClick = async () => {
    if (!user || !user.email) {
      showAlert("Error", "User not authenticated");
      return;
    }

    try {
      // Import the rapidlearningcycles.fmap data
      const response = await fetch("/Data/rapidlearningcycles.fmap");
      const templateData = await response.json();

      // Generate new ID and update metadata
      const newId = `rlc-${Date.now()}`;
      const timestamp = new Date().toISOString();

      const newFlowData = {
        ...templateData,
        id: newId,
        email: user.email,
        timestamp: timestamp,
        name: templateData.name || "Rapid Learning Cycles",
      };

      // Navigate to the new flow with the template data
      navigate(`/flow/new?template=rapidlearningcycles`, {
        state: { data: newFlowData },
      });
    } catch (error) {
      console.error("Error loading Rapid Learning Cycles template:", error);
      showAlert("Error", "Failed to load Rapid Learning Cycles template");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/signin");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleCreateNewFlow = async (
    template: string | null,
    data?: LawyerData
  ) => {
    // if (user) {
    // if (user.email) {
    //   const { limitReached } = await checkUserPlanAndFlowLimit(
    //     user.uid,
    //     user.email,
    //     useStore.getState().useLocalStorage
    //   );
    //   if (limitReached) {
    //     setShowMapLimitAlert(true);
    //     return;
    //   }
    // }
    // }
    // if (template) {
    //   if (data) {
    //     console.log("Creating new flow with data:", data);
    //   }
    //   navigate(`/flow/new?template=${template}`, { state: { data } });
    //   return;
    // }
    navigate("/flow/new");
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleUserProfileClick = async () => {
    if (user) {
      let userData: DocumentData | null = null;
      if (user.email) {
        userData = await getUserDataByEmail(
          user.email,
          useStore.getState().useLocalStorage
        );
      }
      if (userData) {
        // console.log("userData", userData);
        setUserProfile(userData);
        setShowProfileModal(true);
      } else {
        alert("No such document!");
        setShowProfileModal(false);
      }
    }
    setShowMenu(!showMenu);
  };

  const handleSettingsClick = async () => {
    if (user) {
      let userData: DocumentData | null = null;
      if (user.email) {
        userData = await getUserDataByEmail(
          user.email,
          useStore.getState().useLocalStorage
        );
      }
      if (userData) {
        // console.log("userData", userData);
        setUserProfile(userData);
        setUserSettingsVisible(true);
      } else {
        alert("No such document!");
        setUserSettingsVisible(false);
      }
    }
    setShowMenu(!showMenu);
  };

  const handleDbSettingsClick = async () => {
    if (user) {
      let userData: DocumentData | null = null;
      if (user.email) {
        userData = await getUserDataByEmail(
          user.email,
          false //useStore.getState().useLocalStorage
        );
      }
      if (userData) {
        // console.log("userData", userData);
        setDataOnline(userData.useLocalData ? options[0] : options[1]);
        setUserSettingsVisible(true);
      } else {
        alert("No such document!");
        setUserSettingsVisible(false);
      }
    }
  };

  const handlePrivacyPolicyClick = async () => {
    if (user) {
      window.open("https://www.focusmap.online/privacy-policy", "_blank");
    }
    setShowMenu(!showMenu);
  };

  const handleTermsAndConditionsClick = async () => {
    window.open("https://www.focusmap.online/terms-and-coniditions", "_blank");
    setShowMenu(!showMenu);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
  };

  const handleHelp = () => {
    setShowHelpModal(true);
  };

  const handleCloseHelpModal = () => {
    setShowHelpModal(false);
  };

  const handleHelpTopicClick = (topic: string) => {
    setSelectedHelpTopic(topic);
  };

  const handleSubscriptionPlansClick = async () => {
    window.open("https://www.focusmap.online/subscriptions", "_blank");
    setShowMenu(!showMenu);
  };

  const handleCloseVideoModal = () => {
    setSelectedHelpTopic(null);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "unknown date";
    const date = new Date(timestamp.seconds * 1000);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  // const handleFocus365Click = () => {
  //   navigate("/Focus365");
  //   setShowMenu(!showMenu);
  // };

  const handleCloseWelcomeModal = async (showHelp: boolean) => {
    if (doNotShowAgain && user) {
      await updateUserShowWelcome(
        user.uid,
        false,
        useStore.getState().useLocalStorage
      );
    }
    setShowWelcomeModal(false);
    if (showHelp) {
      setShowHelpModal(true);
    }
  };

  // const showUserSettings = async () => {
  //   const authUser = auth.currentUser;
  //   if (authUser) {
  //     const user: any = await getUserDoc(
  //       authUser.uid,
  //       dataOnline == options[0] ? true : false
  //     );
  //     setDataOnline(user?.useLocalData ? options[0] : options[1]);
  //     setUserSettingsVisible(true);
  //   }
  // };

  function saveUserSettings() {
    if (dataOnline === null) {
      showAlert("Alert", "Please select a data storage option");
      return;
    }
    const authUser = auth.currentUser;
    if (authUser) {
      //if (useStore.getState().useLocalStorage === true) {
      updateUserUseLocalData(authUser.uid, dataOnline == options[0]); //dataOnline == options[0]);
      useStore.getState().setUseLocalStorage(dataOnline == options[0]);
      //}
    }

    setUserSettingsVisible(false);
  }

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">User Settings</span>
    </div>
  );

  const footerContent = (
    <div>
      <Button
        label="Ok"
        icon="pi pi-check"
        onClick={() => saveUserSettings()}
        autoFocus
      />
    </div>
  );

  const [userSettingsVisible, setUserSettingsVisible] = useState(false);
  const options = ["Local Data", "Cloud Data"];
  const [dataOnline, setDataOnline] = useState(
    useStore.getState().useLocalStorage ? options[0] : options[1]
  );
  //const [userLocalData, setUseLocalData] = useState(false);

  // Add a state to hold image URLs for each flow
  const [flowImages, setFlowImages] = useState<Record<string, string>>({});
  const [templateImages, setTemplateImages] = useState<Record<string, string>>(
    {}
  );

  // Fetch images for flows after flows are loaded
  useEffect(() => {
    const fetchImages = async () => {
      const images: Record<string, string> = {};
      await Promise.all(
        flows.map(async (flow) => {
          const img = await getImageFromStorage(`${flow.id}.png`);
          if (img) images[flow.id] = img;
        })
      );
      setFlowImages(images);
    };
    if (flows.length > 0) fetchImages();
  }, [flows]);

  // Fetch images for templates after templates are loaded
  useEffect(() => {
    const fetchTemplateImages = async () => {
      const images: Record<string, string> = {};
      await Promise.all(
        templates.map(async (template) => {
          const img = await getImageFromStorage(`${template.id}.png`);
          if (img) images[template.id] = img;
        })
      );
      setTemplateImages(images);
    };
    if (templates.length > 0) fetchTemplateImages();
  }, [templates]);

  return (
    <div className="homepage-container">
      {/* {import.meta.env.VITE_ENV === "DEV" && (
        <img src={devBanner} className="banner" alt="FocusMap Logo" />
      )} */}
      {/* <h1 className="homepage-title" onClick={handleFocus365Click}>
        My Focus
      </h1> */}
      <BurgerMenu
        showMenu={showMenu}
        toggleMenu={toggleMenu}
        handleUserProfileClick={handleUserProfileClick}
        handleSettingsClick={handleSettingsClick}
        //handleFocusMapSiteClick={handleFocus365Click}
        handleSubscriptionPlansClick={handleSubscriptionPlansClick}
        handleTermsAndConditionsClick={handleTermsAndConditionsClick}
        handlePrivacyPolicyClick={handlePrivacyPolicyClick}
        handleSignOut={handleSignOut}
        isAuthenticated={true}
      />
      {showProfileModal && (
        <div className="homepage-modal">
          <div className="homepage-modal-content">
            <span className="homepage-close" onClick={closeProfileModal}>
              &times;
            </span>
            <h2>Profile</h2>
            {userProfile && (
              <div>
                {/* <p>Name: {userProfile.name ? "" : "-"}</p> */}
                <p>Email: {userProfile.email}</p>
                {/* <p>Created: {formatDate(userProfile.createdAt ? userProfile.createdAt : userProfile.timestamp)}</p> */}
                <p>
                  Plan: <strong>{userProfile.plan}</strong>
                  <a
                    href="https://www.focusmap.online/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="subscription-link"
                  >
                    (more plans available <FaExternalLinkAlt /> )
                  </a>
                </p>
                <p>App Version: {import.meta.env.VITE_APP_VERSION}</p>
                <p>
                  Data Storage:{" "}
                  {useStore.getState().useLocalStorage ? (
                    <div title="Local Data">
                      <FaDatabase
                        size={14}
                        color="#3C5973"
                        style={{ marginTop: "5px" }}
                      />{" "}
                      Local
                    </div>
                  ) : (
                    <div title="Cloud Data">
                      <FaCloud size={14} color="#3C5973" /> Cloud
                    </div>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {showSettingsModal && (
        <div className="homepage-modal">
          <div className="homepage-modal-content">
            <span
              className="homepage-close"
              onClick={() => setShowSettingsModal(false)}
            >
              &times;
            </span>
            <h2>Settings</h2>
            {userProfile && (
              <div>
                <p>Name: {userProfile.name}</p>
                <p>Email: {userProfile.email}</p>
              </div>
            )}
          </div>
        </div>
      )}
      <button className="help-button" onClick={handleHelp} title="Help Center">
        <IoHelpCircle size={32} />
      </button>

      <button
        className="data-button"
        onClick={handleDbSettingsClick}
        title="Data Location"
      >
        {useStore.getState().useLocalStorage ? (
          <FaDatabase size={30} />
        ) : (
          <FaCloud size={32} />
        )}
      </button>

      {/* <DataViewLayoutOptions
        layout={layout}
        onChange={(e) => setLayout(e.value as "grid" | "list")}
        className="data-view-layout-options"
      /> */}

      <TabView className="homepage-p-tabview-nav">
        <TabPanel header="My Maps" className="">
          <div className="card">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <input
                type="text"
                placeholder="Search maps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  textAlign: "center",
                  outline: "none",
                  transition: "border-color 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#136556")}
                onBlur={(e) => (e.target.style.borderColor = "#ddd")}
              />
            </div>
            <div className="flows-grid">
              {flows.length === 0 ? (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#888",
                    fontSize: "1.2rem",
                    width: "100%",
                    pointerEvents: "none",
                  }}
                >
                  Click on the bottom right button to create a new map
                </div>
              ) : (
                flows
                  .filter((flow) =>
                    flow.name.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((flow) => (
                    <FlowCard
                      key={flow.id}
                      flow={flow}
                      handleFlowClick={handleFlowClick}
                      reSortData={reSortData}
                      loadFlows={loadFlows}
                    />
                  ))
              )}
            </div>
            {flows.length > 0 &&
              flows.filter((flow) =>
                flow.name.toLowerCase().includes(searchQuery.toLowerCase())
              ).length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: "#888",
                    fontSize: "1rem",
                    marginTop: "40px",
                  }}
                >
                  No maps match your search.
                </div>
              )}
          </div>
        </TabPanel>
        <TabPanel header="My Templates">
          <div className="templates-grid">
            {templates.map(
              (template) =>
                template && (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    templateImage={templateImages[template.id]}
                    handleTemplateClick={(templateId) =>
                      navigate(`/flow/new?template=${templateId}`)
                    }
                    loadTemplates={fetchMyTemplates}
                  />
                )
            )}
          </div>
        </TabPanel>
        <TabPanel header="Integrations & Modules">
          <div className="templates-grid">
            {integrations.map((integration) => {
              // Example: disable all except "onenote" (customize as needed)
              const isDisabled = integration.disabled;
              return (
                <div
                  key={integration.id}
                  style={{
                    opacity: isDisabled ? 0.5 : 1,
                    pointerEvents: isDisabled ? "none" : "auto",
                    filter: isDisabled ? "grayscale(70%)" : "none",
                  }}
                >
                  <IntegrationCard
                    integration={integration}
                    handleIntegrationClick={() => {
                      switch (integration.id) {
                        case "onenote":
                          handleIntegrationClick(integration.id);
                          break;
                        case "lawyer":
                          handleLawyerIntegrationClick(integration.id);
                          break;
                        case "sustainability":
                          handleSustainabilityIntegrationClick(integration.id);
                          break;
                        case "microsoft365":
                          handleMicrosoft365IntegrationClick(integration.id);
                          break;
                        case "rapidlearningcycles":
                          handleRapidLearningCyclesClick();
                          break;
                        default:
                          alert("Work in Progress...");
                          break;
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </TabPanel>
      </TabView>
      <button
        id="create-map"
        title="Create New"
        className="create-flow-button fixed-button"
        onClick={() => handleCreateNewFlow(null)}
      >
        <FaPlus />
      </button>
      <HelpModal showHelpModal={showHelpModal} onClose={handleCloseHelpModal} />
      <WelcomeModal
        showHelpModal={showWelcomeModal}
        setShowHelpModal={setShowWelcomeModal}
        onClose={handleCloseWelcomeModal}
        setDoNotShowAgain={setDoNotShowAgain}
      />

      <LaywerTemplate
        visible={showLawyerTemplate}
        onHide={() => setShowLawyerTemplate(false)}
        onCreateCase={(data: LawyerData) => {
          setShowLawyerTemplate(false);
          handleCreateNewFlow("lawyer", data);
        }}
      />

      <OneNoteIntegrationModal
        show={showOneNoteModal}
        onClose={() => setShowOneNoteModal(false)}
      />
      {showMapLimitAlert && (
        <CustomAlert
          title="Limit Reached"
          message=""
          htmlMessage={
            <>
              You have reached the maximum number of maps for the free plan.
              Please visit our{" "}
              <a
                href="https://www.focusmap.online/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
              >
                subscription page
              </a>{" "}
              for more options.
            </>
          }
          onClose={() => setShowMapLimitAlert(false)}
        />
      )}

      {showFeatureAlert && (
        <CustomAlert
          title="💎Premium Plan Feature"
          message=""
          htmlMessage={
            <>
              Upgrade to a Paid Plan and get immediate access to this feature.
              Please visit our{" "}
              <a
                href="https://www.focusmap.online/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
              >
                subscription page
              </a>{" "}
              for more options.
            </>
          }
          onClose={() => setShowFeatureAlert(false)}
        />
      )}

      {alertMessage && (
        <CustomAlert
          title={alertTitle}
          message={alertMessage}
          htmlMessage={null}
          onClose={handleCloseAlert}
        />
      )}

      <div className="card flex justify-content-center">
        <Dialog
          visible={userSettingsVisible}
          modal
          header={headerElement}
          footer={footerContent}
          style={{ width: "50rem" }}
          onHide={() => {
            setUserSettingsVisible(false);
          }}
        >
          <div className="card flex justify-content-center">
            <SelectButton
              className="flex align-self-center align-content-center align-items-center align-self-center"
              value={dataOnline}
              onChange={(e) => setDataOnline(e.value)}
              options={options}
              style={{
                margin: "10px 0px 0px 0px",
                display: "flex",
                justifyContent: "center",
              }}
            />
            <p
              style={{
                margin: "0 auto",
                display: "flex",
                justifyContent: "center",
              }}
            >
              *data in the cloud offers automatic backup, map sharing and more
            </p>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default Homepage;
